import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DetailSection } from '../ui/DetailSection.js';
import { Button } from '../ui/Button.js';
import { Input } from '../ui/Input.js';
import { Textarea } from '../ui/Textarea.js';
import { IconButton } from '../ui/IconButton.js';
import { LoadingState } from '../ui/LoadingState.js';
import { useConfirm } from '../ui/ConfirmDialog.js';
import { useToast } from '../ui/Toast.js';
import { Pill } from '../PillGroup.js';
import {
  useGenerateContentPlan,
  useUpdateContentPlan,
  useStartDesign,
  useGenerateContentReport,
  type ProjectRow,
  type ContentPlan,
  type ContentBlock,
} from '../../hooks/useProjects.js';
import { useComposeContentPlanEmail, useProjectDrafts, useProjectReplies } from '../../hooks/useProjectOutreach.js';

// ── Inline SVG icons ──────────────────────────────────────────────────────────

function ChevronUp() {
  return (
    <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="18 15 12 9 6 15" />
    </svg>
  );
}

function ChevronDown() {
  return (
    <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function ImageIcon() {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
  );
}

// ── InlineText — click-to-edit in blueprint context ────────────────────────────

interface InlineTextProps {
  value: string;
  onChange: (v: string) => void;
  onSave: () => void;
  placeholder?: string;
  multiline?: boolean;
}

function InlineText({ value, onChange, onSave, placeholder = '—', multiline = false }: InlineTextProps) {
  const [active, setActive] = useState(false);

  const baseStyle: React.CSSProperties = {
    width: '100%',
    background: 'transparent',
    border: 'none',
    borderBottom: '2px solid var(--evo-cyan)',
    outline: 'none',
    fontFamily: 'var(--evo-font)',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    color: 'inherit',
    letterSpacing: 'inherit',
    lineHeight: 'inherit',
    padding: 0,
    display: 'block',
  };

  if (active && multiline) {
    return (
      <Textarea
        variant="naked"
        autoFocus
        value={value}
        rows={3}
        onChange={onChange}
        onBlur={() => { setActive(false); onSave(); }}
        resize="vertical"
        fullWidth
      />
    );
  }

  if (active) {
    return (
      <Input
        variant="naked"
        autoFocus
        value={value}
        onChange={onChange}
        onBlur={() => { setActive(false); onSave(); }}
        fullWidth
      />
    );
  }

  return (
    <span
      role="button"
      tabIndex={0}
      onClick={() => setActive(true)}
      onKeyDown={e => { if (e.key === 'Enter') setActive(true); }}
      title="Click para editar"
      style={{
        display: 'block',
        cursor: 'text',
        minHeight: '1.2em',
        color: value ? 'inherit' : 'var(--evo-fg-4)',
        fontFamily: 'var(--evo-font)',
      }}
    >
      {value || placeholder}
    </span>
  );
}

// ── ImagePlaceholder ──────────────────────────────────────────────────────────

function ImagePlaceholder({ height = 140 }: { height?: number }) {
  return (
    <div style={{
      width: '100%',
      height,
      background: 'var(--evo-bg-2)',
      border: '1px dashed var(--evo-stroke)',
      borderRadius: 'var(--evo-r-sm)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 'var(--evo-space-1)',
      color: 'var(--evo-fg-4)',
      fontFamily: 'var(--evo-font)',
      fontSize: 'var(--evo-fs-label)',
      flexShrink: 0,
    }}>
      <ImageIcon />
      Imagen
    </div>
  );
}

// ── Block controls header bar ─────────────────────────────────────────────────

interface BlockControlsProps {
  block: ContentBlock;
  index: number;
  isFirst: boolean;
  isLast: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

function BlockControls({ block, index, isFirst, isLast, onMoveUp, onMoveDown, onDuplicate, onDelete }: BlockControlsProps) {
  const { t } = useTranslation();
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--evo-space-2)',
      padding: 'var(--evo-space-2) var(--evo-space-3)',
      background: 'var(--evo-bg-2)',
      borderBottom: '1px solid var(--evo-stroke)',
    }}>
      <span style={{
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        background: 'var(--evo-fg-3)',
        color: 'var(--evo-bg)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 'var(--evo-fs-caption)',
        fontFamily: 'var(--evo-font)',
        fontWeight: 'var(--evo-fw-bold)',
        flexShrink: 0,
      }}>
        {index + 1}
      </span>
      <Pill tone="neutral" variant="subtle" size="sm">
        {t(`projects.contentPlan.blockTypes.${block.type}`, { defaultValue: block.type })}
      </Pill>
      {block.layout && (
        <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)' }}>
          {block.layout}
        </span>
      )}
      <div style={{ flex: 1 }} />
      <div style={{ display: 'flex', gap: 'var(--evo-space-1)' }}>
        <IconButton icon={<ChevronUp />} ariaLabel={t('projects.contentPlan.block.moveUp')} tooltip={t('projects.contentPlan.block.moveUp')} variant="ghost" tone="muted" size="sm" disabled={isFirst} onClick={onMoveUp} />
        <IconButton icon={<ChevronDown />} ariaLabel={t('projects.contentPlan.block.moveDown')} tooltip={t('projects.contentPlan.block.moveDown')} variant="ghost" tone="muted" size="sm" disabled={isLast} onClick={onMoveDown} />
        <IconButton icon={<CopyIcon />} ariaLabel={t('projects.contentPlan.block.duplicate')} tooltip={t('projects.contentPlan.block.duplicate')} variant="ghost" tone="muted" size="sm" onClick={onDuplicate} />
        <IconButton icon={<XIcon />} ariaLabel={t('projects.contentPlan.block.delete')} tooltip={t('projects.contentPlan.block.delete')} variant="ghost" tone="danger" size="sm" onClick={onDelete} />
      </div>
    </div>
  );
}

// ── Block body renderers ──────────────────────────────────────────────────────

type PatchFn = (updates: Partial<ContentBlock>) => void;

function HeroBody({ block, patch, save }: { block: ContentBlock; patch: PatchFn; save: () => void }) {
  const hasImage = (block.imagePlaceholders ?? 0) > 0;
  return (
    <div style={{ display: 'flex', gap: 'var(--evo-space-5)', alignItems: 'flex-start' }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)' }}>
        <div style={{ fontSize: 'var(--evo-fs-h1)', fontWeight: 'var(--evo-fw-bold)', lineHeight: 1.05, color: 'var(--evo-fg-1)' }}>
          <InlineText value={block.title} onChange={v => patch({ title: v })} onSave={save} placeholder="Título principal" />
        </div>
        <div style={{ fontSize: 'var(--evo-fs-h3)', fontWeight: 'var(--evo-fw-regular)', color: 'var(--evo-fg-2)', lineHeight: 1.4 }}>
          <InlineText value={block.subtitle} onChange={v => patch({ subtitle: v })} onSave={save} placeholder="Propuesta de valor" />
        </div>
        {block.content && (
          <div style={{ fontSize: 'var(--evo-fs-body-lg)', color: 'var(--evo-fg-3)', lineHeight: 1.6 }}>
            <InlineText value={block.content} onChange={v => patch({ content: v })} onSave={save} placeholder="Descripción" multiline />
          </div>
        )}
        {block.ctaText !== undefined && (
          <div style={{ marginTop: 'var(--evo-space-2)' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              minWidth: '100px',
              height: '36px',
              padding: '0 var(--evo-space-4)',
              background: 'var(--evo-fg-1)',
              borderRadius: 'var(--evo-r-pill)',
              color: 'var(--evo-bg)',
              fontFamily: 'var(--evo-font)',
              fontSize: 'var(--evo-fs-body)',
              fontWeight: 'var(--evo-fw-semibold)',
            }}>
              <InlineText value={block.ctaText} onChange={v => patch({ ctaText: v })} onSave={save} placeholder="Botón CTA" />
            </div>
          </div>
        )}
      </div>
      {hasImage && (
        <div style={{ width: '38%', flexShrink: 0 }}>
          <ImagePlaceholder height={220} />
        </div>
      )}
    </div>
  );
}

function SectionHeader({ block, patch, save }: { block: ContentBlock; patch: PatchFn; save: () => void }) {
  return (
    <div style={{ marginBottom: 'var(--evo-space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
      <div style={{ fontSize: 'var(--evo-fs-h2)', fontWeight: 'var(--evo-fw-bold)', color: 'var(--evo-fg-1)', lineHeight: 1.2 }}>
        <InlineText value={block.title} onChange={v => patch({ title: v })} onSave={save} placeholder="Título de sección" />
      </div>
      {block.subtitle !== undefined && (
        <div style={{ fontSize: 'var(--evo-fs-body-lg)', color: 'var(--evo-fg-3)', lineHeight: 1.5 }}>
          <InlineText value={block.subtitle} onChange={v => patch({ subtitle: v })} onSave={save} placeholder="Subtítulo" />
        </div>
      )}
    </div>
  );
}

function ServicesBody({ block, patch, save }: { block: ContentBlock; patch: PatchFn; save: () => void }) {
  const cols = block.layout === 'two-col' ? 2 : 3;
  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: `repeat(${cols}, 1fr)`,
    gap: 'var(--evo-space-3)',
  };
  return (
    <>
      <SectionHeader block={block} patch={patch} save={save} />
      <div style={gridStyle}>
        {block.items.length > 0 ? block.items.map((item, i) => (
          <div key={i} style={{
            display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)',
            padding: 'var(--evo-space-3)',
            background: 'var(--evo-bg-2)',
            borderRadius: 'var(--evo-r-sm)',
            border: '1px solid var(--evo-stroke)',
          }}>
            <div style={{
              width: '36px', height: '36px', borderRadius: 'var(--evo-r-sm)',
              background: 'var(--evo-stroke)',
            }} />
            <div style={{ fontSize: 'var(--evo-fs-body)', fontWeight: 'var(--evo-fw-semibold)', color: 'var(--evo-fg-1)', fontFamily: 'var(--evo-font)' }}>
              <InlineText value={item.label} onChange={v => patch({ items: block.items.map((it, k) => k === i ? { ...it, label: v } : it) })} onSave={save} placeholder="Servicio" />
            </div>
            <div style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', lineHeight: 1.4 }}>
              <InlineText value={item.description} onChange={v => patch({ items: block.items.map((it, k) => k === i ? { ...it, description: v } : it) })} onSave={save} placeholder="Descripción" multiline />
            </div>
          </div>
        )) : (
          <div style={{ gridColumn: `1 / ${cols + 1}`, color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)', fontSize: 'var(--evo-fs-label)' }}>
            Sin items — pedile a Claude que los agregue
          </div>
        )}
      </div>
    </>
  );
}

function FeaturesBody({ block, patch, save }: { block: ContentBlock; patch: PatchFn; save: () => void }) {
  return (
    <>
      <SectionHeader block={block} patch={patch} save={save} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-4)' }}>
        {block.items.length > 0 ? block.items.map((item, i) => {
          const textLeft = i % 2 === 0;
          const textBlock = (
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 'var(--evo-fw-semibold)', fontSize: 'var(--evo-fs-body-lg)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-1)', marginBottom: 'var(--evo-space-1)' }}>
                <InlineText value={item.label} onChange={v => patch({ items: block.items.map((it, k) => k === i ? { ...it, label: v } : it) })} onSave={save} placeholder="Característica" />
              </div>
              <div style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)' }}>
                <InlineText value={item.description} onChange={v => patch({ items: block.items.map((it, k) => k === i ? { ...it, description: v } : it) })} onSave={save} placeholder="Descripción" multiline />
              </div>
            </div>
          );
          return (
            <div key={i} style={{ display: 'flex', gap: 'var(--evo-space-4)', alignItems: 'center' }}>
              {textLeft && textBlock}
              <div style={{ width: '40%', flexShrink: 0 }}><ImagePlaceholder height={120} /></div>
              {!textLeft && textBlock}
            </div>
          );
        }) : (
          <div style={{ color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)', fontSize: 'var(--evo-fs-label)' }}>
            Sin items — pedile a Claude que los agregue
          </div>
        )}
      </div>
    </>
  );
}

function TestimonialsBody({ block, patch, save }: { block: ContentBlock; patch: PatchFn; save: () => void }) {
  return (
    <>
      <SectionHeader block={block} patch={patch} save={save} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)' }}>
        {block.items.length > 0 ? block.items.map((item, i) => (
          <div key={i} style={{
            padding: 'var(--evo-space-4)',
            background: 'var(--evo-bg-2)',
            borderRadius: 'var(--evo-r-md)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--evo-space-2)',
          }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'var(--evo-stroke)', flexShrink: 0 }} />
            <div style={{ fontFamily: 'var(--evo-font)', fontSize: 'var(--evo-fs-body-lg)', color: 'var(--evo-fg-2)', fontStyle: 'italic', textAlign: 'center', width: '100%', maxWidth: '400px' }}>
              <InlineText value={item.description} onChange={v => patch({ items: block.items.map((it, k) => k === i ? { ...it, description: v } : it) })} onSave={save} placeholder="Testimonio" multiline />
            </div>
            <div style={{ fontFamily: 'var(--evo-font)', fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-3)', fontWeight: 'var(--evo-fw-semibold)' }}>
              — <InlineText value={item.label} onChange={v => patch({ items: block.items.map((it, k) => k === i ? { ...it, label: v } : it) })} onSave={save} placeholder="Nombre" />
            </div>
          </div>
        )) : (
          <div style={{ padding: 'var(--evo-space-5)', background: 'var(--evo-bg-2)', borderRadius: 'var(--evo-r-md)', textAlign: 'center', color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)', fontSize: 'var(--evo-fs-label)' }}>
            Sin testimonios — pedile a Claude que los agregue
          </div>
        )}
        {block.items.length > 1 && (
          <div style={{ display: 'flex', gap: 'var(--evo-space-1)', justifyContent: 'center' }}>
            {block.items.map((_, i) => (
              <div key={i} style={{
                width: i === 0 ? '20px' : '6px',
                height: '6px',
                borderRadius: 'var(--evo-r-pill)',
                background: i === 0 ? 'var(--evo-fg-1)' : 'var(--evo-stroke)',
              }} />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function FaqBody({ block, patch, save }: { block: ContentBlock; patch: PatchFn; save: () => void }) {
  return (
    <>
      <SectionHeader block={block} patch={patch} save={save} />
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {block.items.length > 0 ? block.items.map((item, i) => (
          <div key={i} style={{
            padding: 'var(--evo-space-3) 0',
            borderBottom: '1px solid var(--evo-stroke)',
            display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)',
          }}>
            <div style={{ fontFamily: 'var(--evo-font)', fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-1)', fontWeight: 'var(--evo-fw-medium)' }}>
              <InlineText value={item.label} onChange={v => patch({ items: block.items.map((it, k) => k === i ? { ...it, label: v } : it) })} onSave={save} placeholder="Pregunta" />
            </div>
            <div style={{ fontFamily: 'var(--evo-font)', fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-3)' }}>
              <InlineText value={item.description} onChange={v => patch({ items: block.items.map((it, k) => k === i ? { ...it, description: v } : it) })} onSave={save} placeholder="Respuesta" multiline />
            </div>
          </div>
        )) : (
          <div style={{ color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)', fontSize: 'var(--evo-fs-label)', padding: 'var(--evo-space-3) 0' }}>
            Sin preguntas — pedile a Claude que las agregue
          </div>
        )}
      </div>
    </>
  );
}

function CtaBody({ block, patch, save }: { block: ContentBlock; patch: PatchFn; save: () => void }) {
  return (
    <div style={{ textAlign: 'center', padding: 'var(--evo-space-4) 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--evo-space-3)' }}>
      <div style={{ fontSize: 'var(--evo-fs-h2)', fontWeight: 'var(--evo-fw-bold)', color: 'var(--evo-fg-1)', lineHeight: 1.2, maxWidth: '480px' }}>
        <InlineText value={block.title} onChange={v => patch({ title: v })} onSave={save} placeholder="Título de la CTA" />
      </div>
      {block.subtitle && (
        <div style={{ fontSize: 'var(--evo-fs-body-lg)', color: 'var(--evo-fg-3)', lineHeight: 1.5, maxWidth: '400px' }}>
          <InlineText value={block.subtitle} onChange={v => patch({ subtitle: v })} onSave={save} placeholder="Texto de apoyo" />
        </div>
      )}
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        minWidth: '120px',
        height: '40px',
        padding: '0 var(--evo-space-5)',
        background: 'var(--evo-fg-1)',
        borderRadius: 'var(--evo-r-pill)',
        color: 'var(--evo-bg)',
        fontFamily: 'var(--evo-font)',
        fontSize: 'var(--evo-fs-body)',
        fontWeight: 'var(--evo-fw-semibold)',
        marginTop: 'var(--evo-space-2)',
        justifyContent: 'center',
      }}>
        <InlineText value={block.ctaText} onChange={v => patch({ ctaText: v })} onSave={save} placeholder="Botón CTA" />
      </div>
    </div>
  );
}

function AboutBody({ block, patch, save }: { block: ContentBlock; patch: PatchFn; save: () => void }) {
  const hasImage = (block.imagePlaceholders ?? 0) > 0;
  return (
    <div style={{ display: 'flex', gap: 'var(--evo-space-5)', alignItems: 'flex-start' }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)' }}>
        <div style={{ fontSize: 'var(--evo-fs-h2)', fontWeight: 'var(--evo-fw-bold)', color: 'var(--evo-fg-1)', lineHeight: 1.2 }}>
          <InlineText value={block.title} onChange={v => patch({ title: v })} onSave={save} placeholder="Título" />
        </div>
        {block.subtitle && (
          <div style={{ fontSize: 'var(--evo-fs-body-lg)', color: 'var(--evo-fg-3)', lineHeight: 1.5 }}>
            <InlineText value={block.subtitle} onChange={v => patch({ subtitle: v })} onSave={save} placeholder="Subtítulo" />
          </div>
        )}
        <div style={{ fontSize: 'var(--evo-fs-body-lg)', color: 'var(--evo-fg-2)', lineHeight: 1.7 }}>
          <InlineText value={block.content} onChange={v => patch({ content: v })} onSave={save} placeholder="Descripción del negocio o equipo" multiline />
        </div>
      </div>
      {hasImage && (
        <div style={{ width: '38%', flexShrink: 0 }}>
          <ImagePlaceholder height={200} />
        </div>
      )}
    </div>
  );
}

const CONTACT_DEFAULT_FIELDS = [
  { label: 'Nombre', description: '' },
  { label: 'Email', description: '' },
  { label: 'Mensaje', description: '' },
];

function ContactBody({ block, patch, save }: { block: ContentBlock; patch: PatchFn; save: () => void }) {
  useEffect(() => {
    if (block.items.length === 0) patch({ items: CONTACT_DEFAULT_FIELDS });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fields = block.items.length > 0 ? block.items : CONTACT_DEFAULT_FIELDS;

  return (
    <>
      <SectionHeader block={block} patch={patch} save={save} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)', maxWidth: '480px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
          {fields.map((item, i) => (
            <div key={i} style={{
              height: '36px',
              border: '1px solid var(--evo-stroke)',
              borderRadius: 'var(--evo-r-sm)',
              display: 'flex', alignItems: 'center',
              padding: '0 var(--evo-space-3)',
              color: 'var(--evo-fg-4)',
              fontFamily: 'var(--evo-font)',
              fontSize: 'var(--evo-fs-body)',
            }}>
              <InlineText
                value={item.label}
                onChange={v => patch({ items: fields.map((it, k) => k === i ? { ...it, label: v } : it) })}
                onSave={save}
                placeholder="Campo"
              />
            </div>
          ))}
        </div>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          minWidth: '120px',
          height: '36px',
          padding: '0 var(--evo-space-4)',
          background: 'var(--evo-fg-1)',
          borderRadius: 'var(--evo-r-pill)',
          color: 'var(--evo-bg)',
          fontFamily: 'var(--evo-font)',
          fontSize: 'var(--evo-fs-body)',
          fontWeight: 'var(--evo-fw-semibold)',
          alignSelf: 'flex-start',
        }}>
          <InlineText value={block.ctaText} onChange={v => patch({ ctaText: v })} onSave={save} placeholder="Enviar" />
        </div>
      </div>
    </>
  );
}

function CustomBody({ block, patch, save }: { block: ContentBlock; patch: PatchFn; save: () => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)' }}>
      <div style={{ fontSize: 'var(--evo-fs-h2)', fontWeight: 'var(--evo-fw-bold)', color: 'var(--evo-fg-1)', lineHeight: 1.2 }}>
        <InlineText value={block.title} onChange={v => patch({ title: v })} onSave={save} placeholder="Título de sección" />
      </div>
      {block.subtitle !== undefined && (
        <div style={{ fontSize: 'var(--evo-fs-body-lg)', color: 'var(--evo-fg-3)', lineHeight: 1.5 }}>
          <InlineText value={block.subtitle} onChange={v => patch({ subtitle: v })} onSave={save} placeholder="Subtítulo" />
        </div>
      )}
      <div style={{ fontSize: 'var(--evo-fs-body-lg)', color: 'var(--evo-fg-2)', lineHeight: 1.7 }}>
        <InlineText value={block.content} onChange={v => patch({ content: v })} onSave={save} placeholder="Contenido de la sección" multiline />
      </div>
      {block.items.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)', marginTop: 'var(--evo-space-1)' }}>
          {block.items.map((item, i) => (
            <div key={i} style={{ padding: 'var(--evo-space-3)', background: 'var(--evo-bg-2)', borderRadius: 'var(--evo-r-sm)', border: '1px solid var(--evo-stroke)', display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)' }}>
              <div style={{ fontSize: 'var(--evo-fs-body)', fontWeight: 'var(--evo-fw-semibold)', color: 'var(--evo-fg-1)', fontFamily: 'var(--evo-font)' }}>
                <InlineText value={item.label} onChange={v => patch({ items: block.items.map((it, k) => k === i ? { ...it, label: v } : it) })} onSave={save} placeholder="Item" />
              </div>
              <div style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)' }}>
                <InlineText value={item.description} onChange={v => patch({ items: block.items.map((it, k) => k === i ? { ...it, description: v } : it) })} onSave={save} placeholder="Descripción" multiline />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function renderBlockBody(block: ContentBlock, patch: PatchFn, save: () => void) {
  switch (block.type) {
    case 'hero':         return <HeroBody block={block} patch={patch} save={save} />;
    case 'services':     return <ServicesBody block={block} patch={patch} save={save} />;
    case 'features':     return <FeaturesBody block={block} patch={patch} save={save} />;
    case 'testimonials': return <TestimonialsBody block={block} patch={patch} save={save} />;
    case 'faq':          return <FaqBody block={block} patch={patch} save={save} />;
    case 'cta':          return <CtaBody block={block} patch={patch} save={save} />;
    case 'about':        return <AboutBody block={block} patch={patch} save={save} />;
    case 'contact':      return <ContactBody block={block} patch={patch} save={save} />;
    default:             return <CustomBody block={block} patch={patch} save={save} />;
  }
}

// ── ContentPlanSection ────────────────────────────────────────────────────────

export interface ContentPlanSectionProps {
  project: ProjectRow;
  plan: ContentPlan | null;
  onPlanChange: (p: ContentPlan | null) => void;
  divider?: boolean;
  onDesignStarted?: () => void;
  onReportGenerated?: () => void;
  onSendContentPlan?: () => void;
}

export function ContentPlanSection({ project, plan, onPlanChange, divider = true, onDesignStarted, onReportGenerated, onSendContentPlan }: ContentPlanSectionProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { confirm: confirmAction, dialog: confirmDialog } = useConfirm();

  const generateMut = useGenerateContentPlan();
  const updateMut = useUpdateContentPlan();
  const startDesignMut = useStartDesign();
  const generateReportMut = useGenerateContentReport();
  const sendContentPlanMut = useComposeContentPlanEmail(project.id);
  const { data: draftsData } = useProjectDrafts(project.id);
  const { data: repliesData } = useProjectReplies(project.id);

  const cpReviewDraftIds = new Set(
    (draftsData?.drafts ?? [])
      .filter(d => d.draftType === 'content_plan_review' && d.status === 'sent')
      .map(d => d.id),
  );
  const contentPlanReply = [...(repliesData?.replies ?? [])]
    .sort((a, b) => b.receivedAt - a.receivedAt)
    .find(r => r.matchedDraftId !== null && cpReviewDraftIds.has(r.matchedDraftId)) ?? null;

  const planRef = useRef<ContentPlan | null>(plan);
  useEffect(() => { planRef.current = plan; }, [plan]);

  function setPlan(p: ContentPlan | null) {
    planRef.current = p;
    onPlanChange(p);
  }

  async function autoSave(p: ContentPlan) {
    try {
      await updateMut.mutateAsync({ id: project.id, contentPlan: p });
    } catch { /* non-blocking */ }
  }

  async function handleGenerate() {
    try {
      const result = await generateMut.mutateAsync(project.id);
      setPlan(result.contentPlan);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    }
  }

  async function handleRegenerate() {
    const ok = await confirmAction({
      title: t('projects.contentPlan.regenerate'),
      description: t('projects.contentPlan.regenerateConfirm'),
      confirmLabel: t('projects.contentPlan.regenerate'),
      tone: 'danger',
    });
    if (!ok) return;
    try {
      const result = await generateMut.mutateAsync(project.id);
      setPlan(result.contentPlan);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    }
  }

  async function handleGenerateReport() {
    try {
      const result = await generateReportMut.mutateAsync(project.id);
      setPlan(result.contentPlan);
      toast.success(t('projects.contentPlan.reportGenerated'), {
        action: {
          label: t('projects.contentPlan.viewReport'),
          onClick: () => navigate(`/reports/${result.reportId}`),
        },
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    }
  }

  async function handleStartDesign() {
    try {
      await startDesignMut.mutateAsync(project.id);
      onDesignStarted?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    }
  }

  function updateBlock(idx: number, updated: ContentBlock) {
    if (!planRef.current) return;
    const blocks = planRef.current.blocks.map((b, i) => i === idx ? updated : b);
    setPlan({ ...planRef.current, blocks, lastEditedAt: new Date().toISOString() });
  }

  function saveBlock() {
    if (planRef.current) void autoSave(planRef.current);
  }

  function moveBlock(idx: number, dir: -1 | 1) {
    if (!planRef.current) return;
    const blocks = [...planRef.current.blocks];
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= blocks.length) return;
    [blocks[idx], blocks[swapIdx]] = [blocks[swapIdx]!, blocks[idx]!];
    const reordered = blocks.map((b, i) => ({ ...b, order: i }));
    const p = { ...planRef.current, blocks: reordered, lastEditedAt: new Date().toISOString() };
    setPlan(p);
    void autoSave(p);
  }

  function duplicateBlock(idx: number) {
    if (!planRef.current) return;
    const blocks = [...planRef.current.blocks];
    const orig = blocks[idx]!;
    const dup: ContentBlock = { ...orig, id: `block-${Date.now()}`, order: idx + 1 };
    blocks.splice(idx + 1, 0, dup);
    const renumbered = blocks.map((b, i) => ({ ...b, order: i }));
    const p = { ...planRef.current, blocks: renumbered, lastEditedAt: new Date().toISOString() };
    setPlan(p);
    void autoSave(p);
  }

  async function deleteBlock(idx: number) {
    const ok = await confirmAction({
      title: t('projects.contentPlan.block.delete'),
      description: t('projects.contentPlan.block.deleteConfirm'),
      confirmLabel: t('common.delete', { defaultValue: 'Eliminar' }),
      tone: 'danger',
    });
    if (!ok || !planRef.current) return;
    const blocks = planRef.current.blocks
      .filter((_, i) => i !== idx)
      .map((b, i) => ({ ...b, order: i }));
    const p = { ...planRef.current, blocks, lastEditedAt: new Date().toISOString() };
    setPlan(p);
    void autoSave(p);
  }

  const isGenerating   = generateMut.isPending;
  const isSavingDesign = startDesignMut.isPending;
  const isReporting    = generateReportMut.isPending;
  const canStartDesign = project.status !== 'in_design' && !!plan && plan.blocks.length > 0;

  // ── Empty state ───────────────────────────────────────────────────────────

  if (!plan) {
    return (
      <DetailSection title={t('projects.contentPlan.title')} divider={divider}>
        {isGenerating ? (
          <div style={{ padding: 'var(--evo-space-4) 0' }}>
            <LoadingState size="sm" label={t('projects.contentPlan.generating')} />
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 'var(--evo-space-3)', padding: 'var(--evo-space-3) 0' }}>
            <span style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-text-muted)', fontFamily: 'var(--evo-font)' }}>
              {t('projects.contentPlan.empty')}
            </span>
            <Button tone="cyan" size="sm" onClick={() => { void handleGenerate(); }}>
              {t('projects.contentPlan.generate')}
            </Button>
          </div>
        )}
        {confirmDialog}
      </DetailSection>
    );
  }

  // ── Blueprint view ────────────────────────────────────────────────────────

  const reportAt = plan.reportGeneratedAt ? new Date(plan.reportGeneratedAt).getTime() : 0;
  const editedAt = new Date(plan.lastEditedAt).getTime();
  const reportState: 'none' | 'current' | 'stale' = !plan.reportGeneratedAt
    ? 'none'
    : editedAt <= reportAt
    ? 'current'
    : 'stale';

  return (
    <DetailSection title={t('projects.contentPlan.title')} divider={divider}>
      {confirmDialog}

      {/* Blueprint — stacked blocks */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)' }}>
        {plan.blocks.map((block, idx) => (
          <div
            key={block.id}
            style={{
              border: '1px solid var(--evo-stroke)',
              borderRadius: 'var(--evo-r-md)',
              overflow: 'hidden',
            }}
          >
            <BlockControls
              block={block}
              index={idx}
              isFirst={idx === 0}
              isLast={idx === plan.blocks.length - 1}
              onMoveUp={() => moveBlock(idx, -1)}
              onMoveDown={() => moveBlock(idx, 1)}
              onDuplicate={() => duplicateBlock(idx)}
              onDelete={() => { void deleteBlock(idx); }}
            />
            <div style={{ padding: 'var(--evo-space-5)', background: 'var(--evo-bg)' }}>
              {renderBlockBody(
                block,
                (updates) => updateBlock(idx, { ...block, ...updates }),
                saveBlock,
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Reply indicator — shown above the footer when the client has responded */}
      {contentPlanReply !== null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)', paddingTop: 'var(--evo-space-3)' }}>
          <Pill variant="outline" tone="cyan" size="sm">
            {t('projects.contentPlan.clientReview.replied')}
          </Pill>
          <span style={{ fontSize: 'var(--evo-fs-xs)', opacity: 0.6 }}>
            {new Date(contentPlanReply.receivedAt).toLocaleDateString()}
          </span>
        </div>
      )}

      {/* Footer actions — single row, two groups: plan management | client flow */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--evo-space-2)',
        justifyContent: 'flex-end',
        paddingTop: 'var(--evo-space-4)',
        paddingBottom: 'var(--evo-space-4)',
        flexWrap: 'wrap',
      }}>
        <Button variant="ghost" tone="muted" size="sm" loading={isGenerating} onClick={() => { void handleRegenerate(); }}>
          {t('projects.contentPlan.regenerate')}
        </Button>
        {reportState === 'current' ? (
          <Button
            variant="ghost"
            tone="muted"
            size="sm"
            onClick={() => {
              const target = plan.reportId ?? plan.reportSlug;
              if (target) navigate(`/reports/${target}`);
              else onReportGenerated?.();
            }}
          >
            {t('projects.contentPlan.reportIsUpToDate', { defaultValue: 'Reporte generado · Ver' })} →
          </Button>
        ) : (
          <Button variant="outline" tone="muted" size="sm" loading={isReporting} onClick={() => { void handleGenerateReport(); }}>
            {t('projects.contentPlan.generateReport')}
          </Button>
        )}

        <div style={{ width: 1, height: 20, background: 'var(--evo-fg-3)', margin: '0 var(--evo-space-1)', flexShrink: 0 }} />

        <Button
          variant="outline"
          tone="cyan"
          size="sm"
          loading={sendContentPlanMut.isPending}
          onClick={() => {
            sendContentPlanMut.mutate(undefined, {
              onSuccess: () => onSendContentPlan?.(),
            });
          }}
        >
          {cpReviewDraftIds.size > 0
            ? t('projects.contentPlan.clientReview.resend')
            : t('projects.contentPlan.clientReview.send')}
        </Button>
        <Button
          variant="solid"
          tone="cyan"
          size="sm"
          disabled={!canStartDesign}
          loading={isSavingDesign}
          onClick={() => { void handleStartDesign(); }}
        >
          {t('projects.contentPlan.clientReview.advanceToDesign')}
        </Button>
      </div>
    </DetailSection>
  );
}
